import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { isBackendErrorResponse } from '@/__core/helpers/Error';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Order, CreateOrderRequest, UpdateOrderRequest } from '../models/OrderModel';
import { getOrders, createOrder, updateOrder, deleteOrder, getOrderCodes, getOrderStatuses, getCustomers, getEmployees, getCompanies } from './OrderCRUD';

interface OrderCodeOption {
  value: string;
  label: string;
}
const orderTransactionsInFlight = new Map<string, Promise<any>>();

interface OrderStatusOption {
  value: string;
  label: string;
  code: string;
  categoryName: string;
  categoryCode: string;
  sortOrder: number;
  color?: string | null;
}

interface CustomerOption {
  value: string;
  label: string;
  code: string;
  name: string;
  phone: string;
  email: string;
  customerType: string;
}

interface EmployeeOption {
  value: string;
  label: string;
  code: string;
  name: string;
  status: string;
  jobTitle: string;
  area: string;
  office: string;
}

interface CompanyOption {
  value: string;
  label: string;
  code: string;
  name: string;
  shortName: string;
  companyType: string;
}

interface OrderFilters {
  dateFrom: string;
  dateTo: string;
  status: string;
  paymentStatus: string;
  customer: string;
  orderCode: string;
  amountFrom: string;
  amountTo: string;
  notes: string;
  employee: string;
  supplier: string;
  creationDateFrom: string;
  creationDateTo: string;
  updaterName: string;
  priority: string;
  company: string;
}

interface OrderPagination {
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface OrderState {
  orders: Order[];
  allOrders: Order[];
  pagination: OrderPagination;
  loading: boolean;
  error: string | null;
  selectedOrders: string[];
  currentOrder: Order | null;
  orderTransactions: any[];
  transactionsLoading: boolean;
  orderCodes: OrderCodeOption[];
  orderCodesLoading: boolean;
  orderStatuses: OrderStatusOption[];
  orderStatusesLoading: boolean;
  customers: CustomerOption[];
  customersLoading: boolean;
  employees: EmployeeOption[];
  employeesLoading: boolean;
  companies: CompanyOption[];
  companiesLoading: boolean;
  filters: OrderFilters;
  appliedFilters: OrderFilters;
}

const initialFilters: OrderFilters = {
  dateFrom: '',
  dateTo: '',
  status: '',
  paymentStatus: '',
  customer: '',
  orderCode: '',
  amountFrom: '',
  amountTo: '',
  notes: '',
  employee: '',
  supplier: '',
  creationDateFrom: '',
  creationDateTo: '',
  updaterName: '',
  priority: '',
  company: '',
};

const initialPagination: OrderPagination = {
  count: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalPages: 1,
  hasNext: false,
  hasPrevious: false,
};

const getOrdersFromPayload = (payload: any): Order[] => payload?.results || payload || [];

const getPaginationFromPayload = (payload: any, fallbackOrders: Order[]): OrderPagination => {
  if (!payload || Array.isArray(payload)) {
    const count = fallbackOrders.length;
    return {
      count,
      page: 1,
      pageSize: count || initialPagination.pageSize,
      totalPages: count > 0 ? 1 : initialPagination.totalPages,
      hasNext: false,
      hasPrevious: false,
    };
  }

  const fallbackPageSize = fallbackOrders.length || initialPagination.pageSize;

  return {
    count: payload.count ?? fallbackOrders.length,
    page: payload.page ?? 1,
    pageSize: payload.page_size ?? payload.pageSize ?? fallbackPageSize,
    totalPages: payload.total_pages ?? payload.totalPages ?? 1,
    hasNext: payload.has_next ?? payload.hasNext ?? false,
    hasPrevious: payload.has_previous ?? payload.hasPrevious ?? false,
  };
};

const initialState: OrderState = {
  orders: [],
  allOrders: [],
  pagination: initialPagination,
  loading: false,
  error: null,
  selectedOrders: [],
  currentOrder: null,
  orderTransactions: [],
  transactionsLoading: false,
  orderCodes: [],
  orderCodesLoading: false,
  orderStatuses: [],
  orderStatusesLoading: false,
  customers: [],
  customersLoading: false,
  employees: [],
  employeesLoading: false,
  companies: [],
  companiesLoading: false,
  filters: initialFilters,
  appliedFilters: initialFilters,
};

// Async thunks
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (filters?: any) => {
    const response = await getOrders(filters);
    return response.data;
  }
);

export const fetchAllOrders = createAsyncThunk(
  'orders/fetchAllOrders',
  async () => {
    const response = await getOrders();
    return response.data;
  }
);

export const createOrderAsync = createAsyncThunk(
  'orders/createOrder',
  async (orderData: CreateOrderRequest, { rejectWithValue }) => {
    try {
      const response = await createOrder(orderData);
      if (isBackendErrorResponse(response)) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: error.message || 'Failed to create order' });
    }
  }
);

export const updateOrderAsync = createAsyncThunk(
  'orders/updateOrder',
  async ({ id, orderData }: { id: string; orderData: UpdateOrderRequest }, { rejectWithValue }) => {
    try {
      const response = await updateOrder(id, orderData);
      if (isBackendErrorResponse(response)) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error: any) {
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: error.message || 'Failed to update order' });
    }
  }
);

export const deleteOrderAsync = createAsyncThunk(
  'orders/deleteOrder',
  async (id: string) => {
    await deleteOrder(id);
    return id;
  }
);

export const deleteOrdersAsync = createAsyncThunk(
  'orders/deleteOrders',
  async (ids: string[]) => {
    const deletePromises = ids.map(id => deleteOrder(id));
    await Promise.all(deletePromises);
    return ids;
  }
);

export const fetchOrderTransactions = createAsyncThunk(
  'orders/fetchOrderTransactions',
  async (orderId: string) => {
    if (!orderId) {
      return [];
    }

    const inflight = orderTransactionsInFlight.get(orderId);
    if (inflight) {
      return inflight;
    }

    const request = (async () => {
    try {
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      const response = await axiosInstance.get(`/finance/transactions/?order=${orderId}&no_pagination=true`);
      return response.data;
    } catch (error) {
      throw error;
    }
    })();

    orderTransactionsInFlight.set(orderId, request);
    try {
      return await request;
    } finally {
      if (orderTransactionsInFlight.get(orderId) === request) {
        orderTransactionsInFlight.delete(orderId);
      }
    }
  }
);

export const fetchOrderCodes = createAsyncThunk(
  'orders/fetchOrderCodes',
  async (params?: string | { search?: string; company?: string }) => {
    const search = typeof params === 'string' ? params : params?.search;
    const company = typeof params === 'string' ? undefined : params?.company;
    const orderCodes = await getOrderCodes(search, company);
    return orderCodes;
  }
);

export const fetchOrderStatuses = createAsyncThunk(
  'orders/fetchOrderStatuses',
  async () => {
    const orderStatuses = await getOrderStatuses();
    return orderStatuses;
  }
);

export const fetchCustomers = createAsyncThunk(
  'orders/fetchCustomers',
  async (params?: string | { search?: string; company?: string }) => {
    const search = typeof params === 'string' ? params : params?.search;
    const company = typeof params === 'string' ? undefined : params?.company;
    const customers = await getCustomers(search, company);
    return customers;
  }
);

export const fetchEmployees = createAsyncThunk(
  'orders/fetchEmployees',
  async (params?: string | { search?: string; company?: string }) => {
    if (typeof params === 'string') {
      const employees = await getEmployees(params);
      return employees;
    }

    const employees = await getEmployees(params?.search, params?.company);
    return employees;
  }
);

export const fetchCompanies = createAsyncThunk(
  'orders/fetchCompanies',
  async (search?: string) => {
    const companies = await getCompanies(search);
    return companies;
  }
);

export const fetchOrdersWithFilters = createAsyncThunk(
  'orders/fetchOrdersWithFilters',
  async (filters: OrderFilters) => {
    const response = await getOrders(filters);
    return response.data;
  }
);

// Slice
const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    // Selection actions
    selectOrder: (state, action: PayloadAction<string>) => {
      const orderId = action.payload;
      if (state.selectedOrders.includes(orderId)) {
        state.selectedOrders = state.selectedOrders.filter(id => id !== orderId);
      } else {
        state.selectedOrders.push(orderId);
      }
    },
    selectAllOrders: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.selectedOrders = state.orders.map(order => order.id);
      } else {
        state.selectedOrders = [];
      }
    },
    clearSelectedOrders: (state) => {
      state.selectedOrders = [];
    },
    // Current order actions
    setCurrentOrder: (state, action: PayloadAction<Order | null>) => {
      state.currentOrder = action.payload;
    },
    // Error handling
    clearError: (state) => {
      state.error = null;
    },
    // Clear transactions
    clearOrderTransactions: (state) => {
      state.orderTransactions = [];
    },
    // Filter actions
    setFilter: (state, action: PayloadAction<{ field: keyof OrderFilters; value: string }>) => {
      state.filters[action.payload.field] = action.payload.value;
    },
    setFilters: (state, action: PayloadAction<Partial<OrderFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialFilters;
    },
    applyFilters: (state) => {
      state.appliedFilters = { ...state.filters };
    },
    clearAppliedFilters: (state) => {
      state.appliedFilters = initialFilters;
    },
    clearRequestLoading: (state) => {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch orders
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        const orders = getOrdersFromPayload(action.payload);
        state.orders = orders;
        state.pagination = getPaginationFromPayload(action.payload, orders);
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch orders';
      });

    // Fetch all orders
    builder
      .addCase(fetchAllOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.allOrders = action.payload.results || action.payload;
      })
      .addCase(fetchAllOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch all orders';
      });

    // Create order
    builder
      .addCase(createOrderAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrderAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.unshift(action.payload);
        state.allOrders.unshift(action.payload);
      })
      .addCase(createOrderAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create order';
      });

    // Update order
    builder
      .addCase(updateOrderAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderAsync.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload;
        
        // Check if updatedOrder exists and has an id
        if (!updatedOrder || !updatedOrder.id) {
          return;
        }
        
        const orderIndex = state.orders.findIndex(order => order.id === updatedOrder.id);
        if (orderIndex !== -1) {
          state.orders[orderIndex] = updatedOrder;
        }
        const allOrderIndex = state.allOrders.findIndex(order => order.id === updatedOrder.id);
        if (allOrderIndex !== -1) {
          state.allOrders[allOrderIndex] = updatedOrder;
        }
        if (state.currentOrder?.id === updatedOrder.id) {
          state.currentOrder = updatedOrder;
        }
      })
      .addCase(updateOrderAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update order';
      });

    // Delete order
    builder
      .addCase(deleteOrderAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOrderAsync.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.orders = state.orders.filter(order => order.id !== deletedId);
        state.allOrders = state.allOrders.filter(order => order.id !== deletedId);
        state.selectedOrders = state.selectedOrders.filter(id => id !== deletedId);
        if (state.currentOrder?.id === deletedId) {
          state.currentOrder = null;
        }
      })
      .addCase(deleteOrderAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete order';
      });

    // Delete multiple orders
    builder
      .addCase(deleteOrdersAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOrdersAsync.fulfilled, (state, action) => {
        state.loading = false;
        const deletedIds = action.payload;
        state.orders = state.orders.filter(order => !deletedIds.includes(order.id));
        state.allOrders = state.allOrders.filter(order => !deletedIds.includes(order.id));
        state.selectedOrders = state.selectedOrders.filter(id => !deletedIds.includes(id));
        if (state.currentOrder && deletedIds.includes(state.currentOrder.id)) {
          state.currentOrder = null;
        }
      })
      .addCase(deleteOrdersAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete orders';
      });

    // Fetch order transactions
    builder
      .addCase(fetchOrderTransactions.pending, (state) => {
        state.transactionsLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderTransactions.fulfilled, (state, action) => {
        state.transactionsLoading = false;
        const transactions = action.payload.results || action.payload || [];
        // Normalize transaction data - handle both snake_case and camelCase
        state.orderTransactions = transactions.map((transaction: any) => ({
          ...transaction,
          // Ensure total_amount is properly mapped
          total_amount: transaction.total_amount !== undefined ? transaction.total_amount : (transaction.totalAmount !== undefined ? transaction.totalAmount : 0),
          // Ensure actual_amount is properly mapped
          actual_amount: transaction.actual_amount !== undefined ? transaction.actual_amount : (transaction.actualAmount !== undefined ? transaction.actualAmount : 0),
        }));
      })
      .addCase(fetchOrderTransactions.rejected, (state, action) => {
        state.transactionsLoading = false;
        state.error = action.error.message || 'Failed to fetch order transactions';
      });

    // Fetch order codes
    builder
      .addCase(fetchOrderCodes.pending, (state) => {
        state.orderCodesLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderCodes.fulfilled, (state, action) => {
        state.orderCodesLoading = false;
        state.orderCodes = action.payload;
      })
      .addCase(fetchOrderCodes.rejected, (state, action) => {
        state.orderCodesLoading = false;
        state.error = action.error.message || 'Failed to fetch order codes';
      });

    // Fetch order statuses
    builder
      .addCase(fetchOrderStatuses.pending, (state) => {
        state.orderStatusesLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderStatuses.fulfilled, (state, action) => {
        state.orderStatusesLoading = false;
        state.orderStatuses = action.payload;
      })
      .addCase(fetchOrderStatuses.rejected, (state, action) => {
        state.orderStatusesLoading = false;
        state.error = action.error.message || 'Failed to fetch order statuses';
      });

    // Fetch customers
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.customersLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.customersLoading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.customersLoading = false;
        state.error = action.error.message || 'Failed to fetch customers';
      });

    // Fetch employees
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.employeesLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.employeesLoading = false;
        state.employees = action.payload;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.employeesLoading = false;
        state.error = action.error.message || 'Failed to fetch employees';
      });

    // Fetch companies
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.companiesLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.companiesLoading = false;
        state.companies = action.payload;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.companiesLoading = false;
        state.error = action.error.message || 'Failed to fetch companies';
      });

    // Fetch orders with filters
    builder
      .addCase(fetchOrdersWithFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersWithFilters.fulfilled, (state, action) => {
        state.loading = false;
        const orders = getOrdersFromPayload(action.payload);
        state.orders = orders;
        state.pagination = getPaginationFromPayload(action.payload, orders);
      })
      .addCase(fetchOrdersWithFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch orders with filters';
      });
  },
});

export const {
  selectOrder,
  selectAllOrders,
  clearSelectedOrders,
  setCurrentOrder,
  clearError,
  clearOrderTransactions,
  setFilter,
  setFilters,
  clearFilters,
  applyFilters,
  clearAppliedFilters,
  clearRequestLoading,
} = orderSlice.actions;

// Export actions object for compatibility
export const actions = {
  requestOrders: () => ({ type: 'orders/requestOrders' }),
  fulfillOrders: (orders: Order[]) => ({ type: 'orders/fulfillOrders', payload: orders }),
  createOrder: (order: Order) => ({ type: 'orders/createOrder', payload: order }),
  updateOrder: (order: Order) => ({ type: 'orders/updateOrder', payload: order }),
  deleteOrder: (id: string) => ({ type: 'orders/deleteOrder', payload: id }),
};

export default orderSlice.reducer;
